import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Progress, Icon, Popup } from 'semantic-ui-react';
import { colors } from 'utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line import/extensions
import { faBrain } from '@fortawesome/free-solid-svg-icons';

const PROFILE_DESCRIPTIONS = {
  capacity: {
    Low: 'You may have significant financial constraints that limit your ability to absorb investment losses. A short time horizon, high debt, unstable income, or immediate liquidity needs make capital preservation essential.',
    'Low to Moderate':
      'You may have a financial position with some vulnerabilities that restrict your capacity to accept losses. Near-term cash needs, moderate debt, or dependent obligations reduce your ability to withstand meaningful portfolio declines.',
    Moderate:
      'You may have a reasonably stable financial position with a moderate capacity to accept losses. Manageable debt, adequate liquidity, and a medium time horizon allow you to absorb periodic declines without jeopardizing financial security.',
    'Moderate to High':
      'You may have a solid financial foundation with a good capacity to accept losses. A longer time horizon, low-to-moderate debt, and stable income allow you to absorb meaningful short-term declines with limited impact on your goals.',
    High: 'You may have a strong financial position across all dimensions, reflecting a high capacity to accept losses. A long time horizon, high net worth, low debt, and stable income allow you to absorb significant portfolio volatility comfortably.',
  },
  tolerance: {
    Low: 'You prioritize the safety of your capital. The possibility of losing any portion of your investment is uncomfortable. You prefer predictable returns even if that means much slower growth. Protecting what you have matters more to you.',
    'Low to Moderate':
      'You are willing to accept some risk in exchange for modest growth potential. While you remain cautious, you understand that some fluctuations in portfolio values are necessary to achieve higher returns.',
    Moderate:
      'You seek a balance between growth and stability. You are comfortable with some fluctuations and can tolerate periods of modest declines. You understand that short-term volatility is a normal part of pursuing long-term goals.',
    'Moderate to High':
      'You are comfortable accepting meaningful fluctuations in pursuit of stronger long-term growth. You understand that markets move in cycles. You can tolerate periods of market declines without making reactive decisions.',
    High: 'You are focused on maximizing long-term growth and are prepared to accept significant short-term volatility in pursuit of that goal. You are not unsettled by market downturns, and maintain a long-term perspective through all market cycles.',
  },
};

const BEHAVIOURAL_TRAIT_DESCRIPTIONS = {
  fomo: '"Fear of missing out" may trigger impulsive investment decisions due to the fear of missing out on a profitable opportunity that others are in on.',
  recencyBias:
    'The investor may make buy and sell decisions based on recent trends, ignoring longer-term potentials and goals.',
};

const PROFILE_EQUITY = {
  'Very Conservative': 20,
  Conservative: 40,
  Balanced: 60,
  Growth: 80,
  'Aggressive Growth': 100,
};

const CAPACITY_MAX = 25;
const TOLERANCE_MAX = 24;

class ResultsNew extends Component {
  constructor(props) {
    super(props);
    this.state = { accordionOpen: false };
  }

  toggleAccordion = () => {
    this.setState((prev) => ({ accordionOpen: !prev.accordionOpen }));
  };

  render() {
    const {
      plan,
      capacityLabel,
      capacityScore,
      toleranceLabel,
      toleranceScore,
      fomoDetected,
      recencyBiasDetected,
    } = this.props;
    const { accordionOpen } = this.state;

    const equityPct = PROFILE_EQUITY[plan] || 60;
    const capacityPct = Math.round((capacityScore / CAPACITY_MAX) * 100);
    const tolerancePct = Math.round((toleranceScore / TOLERANCE_MAX) * 100);

    return (
      <span>
        <Intro>
          Based on your responses, here&apos;s where investors with similar
          characteristics typically fall.
        </Intro>

        <SectionLabel style={{ marginBottom: '1.5rem' }}>
          YOUR RISK SCORES
        </SectionLabel>

        <CardRow>
          <RiskCard>
            <CardTitle>Risk Capacity</CardTitle>
            <CardSubtitle>
              Based on the answers you provided, your risk capacity can be
              considered:
            </CardSubtitle>
            <ScoreBox>
              <ScoreHeader>
                <ScoreBold>{capacityLabel}</ScoreBold>
                <ScoreRatio>
                  {capacityScore}/{CAPACITY_MAX}
                </ScoreRatio>
              </ScoreHeader>
              <BarTrack>
                <BarFill pct={capacityPct} barColor={colors.darkBlue} />
              </BarTrack>
              <ScoreDesc>
                {PROFILE_DESCRIPTIONS.capacity[capacityLabel]}
              </ScoreDesc>
            </ScoreBox>
          </RiskCard>

          <RiskCard>
            <CardTitle>Risk Tolerance</CardTitle>
            <CardSubtitle>
              Based on the answers you provided, your risk tolerance can be
              considered:
            </CardSubtitle>
            <ScoreBox>
              <ScoreHeader>
                <ScoreBold>{toleranceLabel}</ScoreBold>
                <ScoreRatio>
                  {toleranceScore}/{TOLERANCE_MAX}
                </ScoreRatio>
              </ScoreHeader>
              <BarTrack>
                <BarFill pct={tolerancePct} barColor="#E8A838" />
              </BarTrack>
              <ScoreDesc>
                {PROFILE_DESCRIPTIONS.tolerance[toleranceLabel]}
              </ScoreDesc>
            </ScoreBox>
          </RiskCard>
          {/* Behavioural Traits Card */}
          <BehaviouralCard>
            <BehaviouralCardTitle>
              <BrainIcon>
                <FontAwesomeIcon icon={faBrain} />
              </BrainIcon>
              Behavioural Traits
            </BehaviouralCardTitle>
            <TraitChipsWrapper>
              {/* FOMO Chip */}
              <TraitChipRow>
                <TraitChip>
                  <TraitDot detected={fomoDetected} />
                  <TraitChipLabel>
                    FOMO: {fomoDetected ? 'Detected' : 'Not detected'}
                  </TraitChipLabel>
                  <Popup
                    trigger={
                      <Icon
                        name="info circle"
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#888',
                          marginBottom: '4px',
                        }}
                      />
                    }
                    position="right center"
                    wide
                  >
                    {BEHAVIOURAL_TRAIT_DESCRIPTIONS.fomo}
                  </Popup>
                </TraitChip>
              </TraitChipRow>

              {/* Recency Bias Chip */}
              <TraitChipRow>
                <TraitChip>
                  <TraitDot detected={recencyBiasDetected} />
                  <TraitChipLabel>
                    Recency bias:{' '}
                    {recencyBiasDetected ? 'Detected' : 'Not detected'}
                  </TraitChipLabel>

                  <Popup
                    trigger={
                      <Icon
                        name="info circle"
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#888',
                          marginBottom: '4px',
                        }}
                      />
                    }
                    position="right center"
                    wide
                  >
                    {BEHAVIOURAL_TRAIT_DESCRIPTIONS.recencyBias}
                  </Popup>
                </TraitChip>
              </TraitChipRow>
            </TraitChipsWrapper>
            <BehaviouralDesc>
              These behavioural tendencies can negatively influence investment
              decisions. Self-awareness and coaching should be a priority.
            </BehaviouralDesc>
          </BehaviouralCard>
        </CardRow>

        <AccordionRow onClick={this.toggleAccordion}>
          <Icon
            name="question circle outline"
            style={{
              fontSize: '14px',
              color: 'dimgrey',
              cursor: 'pointer',
              marginBottom: '4px',
            }}
          />
          <AccordionQuestion>
            Why do we assess both risk capacity and tolerance?
          </AccordionQuestion>
          <CaretIcon expanded={accordionOpen} />
        </AccordionRow>
        {accordionOpen && (
          <AccordionBody>
            Your overall risk profile depends on both factors.{' '}
            <strong>Capacity</strong> looks at your ability to accept risk,
            given your financial situation. <strong>Tolerance</strong> looks at
            your willingness to accept risk, such as personal comfort with
            market swings. These two factors do not have to agree. Someone early
            in their career with decades to invest may have high capacity, but
            low tolerance. Someone near retirement may feel willing to ride out
            volatility, but have limited capacity to recover from losses. In
            these cases, the more conservative classification is used as a
            starting point.
          </AccordionBody>
        )}
        <ResultsBox>
          <SectionLabel>YOUR OVERALL RISK PROFILE RESULT</SectionLabel>
          <OverallPlan>
            <strong>{plan}</strong>{' '}
            <span style={{ fontWeight: 400 }}>
              ({capacityScore < toleranceScore ? capacityLabel : toleranceLabel}
              )
            </span>
          </OverallPlan>
          <StyledProgress percent={equityPct} color={colors.blue} progress />
          {capacityLabel !== toleranceLabel && (
            <HintText>
              Your risk capacity and risk tolerance are in different categores.
              We take the more conservative of the two as your overall risk
              profile.
            </HintText>
          )}
          <HintText>
            In the next step, you will have the option to select a different
            profile.
          </HintText>
        </ResultsBox>
      </span>
    );
  }
}

ResultsNew.propTypes = {
  plan: PropTypes.string.isRequired,
  capacityLabel: PropTypes.string.isRequired,
  capacityScore: PropTypes.number.isRequired,
  toleranceLabel: PropTypes.string.isRequired,
  toleranceScore: PropTypes.number.isRequired,
  fomoDetected: PropTypes.bool,
  recencyBiasDetected: PropTypes.bool,
};

ResultsNew.defaultProps = { fomoDetected: false, recencyBiasDetected: false };

export default ResultsNew;

const Intro = styled.p`
  font-size: 16px;
  color: black;
  margin-bottom: 1rem;
`;

const SectionLabel = styled.p`
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  color: black;
  margin-bottom: 0.5rem;
`;

const CardRow = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const RiskCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
`;

const CardSubtitle = styled.p`
  font-size: 16px;
  color: black;
  margin: 0 0 0.75rem 0;
`;

const ScoreBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  flex-grow: 1;
`;

const ScoreHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ScoreBold = styled.span`
  font-size: 16px;
  font-weight: 700;
`;

const ScoreRatio = styled.span`
  font-size: 16px;
  color: #666;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #d0d0d0;
  border-radius: 50px;
  margin-bottom: 0.75rem;
  overflow: hidden;
`;

const BarFill = styled.div`
  width: ${(props) => props.pct}%;
  height: 100%;
  background: ${(props) => props.barColor};
  border-radius: 50px;
`;

const ScoreDesc = styled.p`
  font-size: 16px;
  color: #333;
  margin: 0;
  line-height: 1.5;
`;

const AccordionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 0;
  user-select: none;
`;

const AccordionQuestion = styled.span`
  flex: 1;
  font-size: 16px;
  color: #333;
`;

const AccordionBody = styled.p`
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  padding: 0.75rem 0 0.5rem 0;
  margin: 0;
`;

const OverallPlan = styled.h2`
  font-size: 21px;
  margin: 0.25rem 0 0.75rem 0;
`;

const HintText = styled.p`
  font-size: 16px;
  color: #555;
  margin-bottom: 1rem;
`;

const CaretIcon = styled(Icon).attrs({
  name: 'caret down',
  size: 'large',
})`
  transition: transform 0.25s ease;
  transform: rotate(${(props) => (props.expanded ? '-180deg' : '0deg')});
`;

const StyledProgress = styled(({ className, children, color, ...rest }) => (
  <Progress className={className} {...rest}>
    {children}
  </Progress>
))`
  color: white !important;
  opacity: 100% !important;
  margin-bottom: 10px !important;
  background-color: ${colors.periwinkle} !important;
  height: 30px;
  .bar {
    background-color: ${colors.darkBlue} !important;
    height: 30px !important;
    border-top-right-radius: ${(props) =>
      props.percent >= 100 ? '.28571429rem' : '0'} !important;
    border-bottom-right-radius: ${(props) =>
      props.percent >= 100 ? '.28571429rem' : '0'} !important;
    .progress {
      display: none !important;
    }
    &::after {
      content: '${(props) => props.percent}% Equity';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-weight: bold;
      font-size: 16px;
      white-space: nowrap;
      pointer-events: none;
    }
  }
  &::after {
    content: '${(props) =>
      props.percent === 100 ? '' : `${100 - props.percent}% Fixed Income`}';
    position: absolute;
    left: calc(
      ${(props) => props.percent}% + (100% - ${(props) => props.percent}%) / 2
    );
    top: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-weight: bold;
    font-size: 16px;
    white-space: nowrap;
    pointer-events: none;
  }
`;

const ResultsBox = styled.div`
  background: #f8f8f8;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  flex-grow: 1;
  margin-top: 2rem;
`;

const BehaviouralCard = styled(RiskCard)`
  flex: none;
  width: 290px;
  background: rgb(245, 245, 251);
  border: 1px solid #d0d4e8;
  border-radius: 6px;
  padding: 1rem 1.25rem;
`;

const BehaviouralCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const BrainIcon = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 18px;
  color: #555;
`;

const TraitChipsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const TraitChipRow = styled.div`
  display: flex;
`;

const TraitChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #ebeaf5;
  border-radius: 50px;
  padding: 0.35rem 0.85rem 0.35rem 0.6rem;
  font-size: 14px;
  color: #333;
  border: 1px solid #d0d4e8;
`;

const TraitDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => (props.detected ? colors.red : '#ccc')};
`;

const TraitChipLabel = styled.span`
  font-size: 14px;
  color: #333;
`;

const BehaviouralDesc = styled.p`
  font-size: 16px;
  color: #333;
  margin: 0;
  line-height: 1.5;
`;
